import React, { useEffect, useState } from "react";
import {
  XMarkIcon,
  PlusIcon,
  ArrowLeftIcon,
} from "@heroicons/react/24/outline";
import { SERVER_URL } from "../config";
import SearchableDropdown from "./SearchableDropdown";
import TableHeaderFilter from "./TableHeaderfilter";
import ExcelExport from "./ExcelExport";

const inputStyle =
  "w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 h-10 text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-slate-100";

/* ── status badge ────────────────────────────────────────────────────────────── */
const getStatusStyle = (status) => {
  const s = String(status || "").toLowerCase();
  if (s.includes("open") || s.includes("scheduled")) return "bg-amber-50 text-amber-700 border border-amber-200";
  else if (s.includes("in progress")) return "bg-blue-50 text-blue-700 border border-blue-200";
  else if (s.includes("closed")) return "bg-rose-50 text-rose-700 border border-rose-200";
  else if (s.includes("successful")) return "bg-emerald-50 text-emerald-700 border border-emerald-200";
  else return "bg-slate-50 text-slate-600 border border-slate-200";
};

/* ════════════════════════════════════════════════════════════════════════════ */
const FollowUpModal = ({ isOpen, onClose, onRefresh }) => {

  const employee = JSON.parse(localStorage.getItem("employee")) || {
    employee_name: "",
    employee_id: "",
  };

  const [view, setView] = useState("list");
  const [isNewRoot, setIsNewRoot] = useState(false);
  const [latestFollowUps, setLatestFollowUps] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [selectedFollowUpId, setSelectedFollowUpId] = useState(null);
  const [assignedToOptions, setAssignedToOptions] = useState([]);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [filteredLatest, setFilteredLatest] = useState([]);
  const [filters, setFilters] = useState({});
  const [productOptions, setProductOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState("");
  const [totalCount, setTotalCount] = useState(0);
  const [leadStatusOptions, setLeadStatusOptions] = useState([]);
  const [allProducts, setAllProducts] = useState([]);

  const entityOptions = [
    { label: "Lead", value: "Lead" },
    { label: "Customer", value: "Customer" },
  ];

  const followUpTypeOptions = [
    { label: "Call", value: "Call" },
    { label: "Visit", value: "Visit" },
    { label: "Meeting", value: "Meeting" },
    { label: "Email", value: "Email" },
  ];

  const statusOptions = [
    { label: "Scheduled", value: "Open" },
    { label: "In Progress", value: "In Progress" },
    { label: "Technical Visit Required", value: "Technical Visit Required" },
    { label: "Technical Engineer Visited", value: "Technical Engineer Visited" },
    { label: "Closed", value: "Closed" },
    { label: "Successful", value: "Successful" },
  ];

  // Lead status options for syncing
  const leadStatusList = [
    { label: "New Lead", value: "New Lead" },
    { label: "Estimate Required", value: "Estimate Required" },
    { label: "Estimate Submitted", value: "Estimate Submitted" },
    { label: "Follow up Running", value: "Follow up Running" },
    { label: "Negotiation", value: "Negotiation" },
    { label: "Order Confirmed", value: "Order Confirmed" },
    { label: "Completed & Review Collected", value: "Completed & Review Collected" },
    { label: "Not Interested", value: "Not Interested" },
    { label: "Lost", value: "Lost" },
  ];

  const emptyForm = {
    category: '',
    product_name: "", 
    customer_name: "", 
    entityType: "", 
    nextFollowUp: "",
    followUpType: "", 
    purpose: "", 
    assignedTo: "", 
    status: "", 
    leadStatus: "",
    outcome: "",
  };

  const [form, setForm] = useState(emptyForm);

  const getColumnOptions = (key) => {
    if (!latestFollowUps?.length) return [];
    const unique = [...new Set(latestFollowUps.map((item) => item?.[key]).filter((v) => v !== null && v !== undefined && v !== ""))];
    return unique.map((val) => ({ label: String(val), value: String(val) }));
  };

  const fetchLatest = async () => {
    try {
      const res = await fetch(`${SERVER_URL}/Rahul/followUpHistory.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: employee.employee_id }),
      });
      const data = await res.json();
      if (data.status) { 
        setLatestFollowUps(data.data); 
        setFilteredLatest(data.data);
        if (data.employees) {
          setAssignedToOptions(data.employees);
        }
      }
    } catch (err) { console.error("fetchLatest error:", err); }
  };

  const fetchTimeline = async (id) => {
    try {
      const res = await fetch(`${SERVER_URL}/Rahul/followUpHistory.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ follow_up_id: id, userId: employee.employee_id }),
      });
      const data = await res.json();
      if (data.status) {
        setTimeline(data.data);
        if (data.employees) {
          setAssignedToOptions(data.employees);
        }
      }
    } catch (err) { console.error("fetchTimeline error:", err); }
  };

  const fetchProductsAndCategories = async () => {
    try {
      const res = await fetch(`${SERVER_URL}/Rahul/fetchProductAndCategory.php`);
      const data = await res.json();
      if (data.success) {
        const products = data.products || [];
        setAllProducts(products);
        
        // Extract unique categories
        const categories = [...new Set(products.map(p => p.service).filter(Boolean))];
        setCategoryOptions(categories.map(cat => ({ label: cat, value: cat })));
        
        // Set product options (will be filtered by category)
        setProductOptions(products.map(p => ({ 
          label: p.name, 
          value: p.name,
          category: p.service 
        })));
      }
    } catch (err) { console.error("fetchProducts error:", err); }
  };

  const fetchCustomers = async (product) => {
    try {
      const res = await fetch(`${SERVER_URL}/Rahul/getCustomers.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ product_name: product }),
      });
      const data = await res.json();
      if (data.status) setCustomerOptions(data.data.map((c) => ({ label: c.company_name, value: c.company_name })));
    } catch (err) { console.error("fetchCustomers error:", err); }
  };

  const getFilteredProducts = (category) => {
    if (!category) return [];
    const filtered = allProducts
      .filter(p => p.service === category && p.status === 'ACTIVE')
      .map(p => ({ label: p.name, value: p.name }));
    return filtered;
  };

  useEffect(() => {
    if (!isOpen) return;
    fetchLatest();
    fetchProductsAndCategories();
    setView("list");
    setForm(emptyForm);
    setTimeline([]);
    setSelectedFollowUpId(null);
    setLeadStatusOptions(leadStatusList);
  }, [isOpen]);

  useEffect(() => {
    let temp = [...latestFollowUps];
    Object.keys(filters).forEach((key) => {
      const selected = filters[key];
      if (selected && selected.length > 0) temp = temp.filter((row) => selected.includes(String(row?.[key])));
    });
    if (selectedMonth) {
      const [year, month] = selectedMonth.split("-");
      temp = temp.filter((row) => {
        if (!row.follow_up_date_time) return false;
        const d = new Date(row.follow_up_date_time);
        return d.getFullYear() === Number(year) && d.getMonth() + 1 === Number(month);
      });
    }
    setFilteredLatest(temp);
    setTotalCount(temp.length);
  }, [filters, latestFollowUps, selectedMonth]);

  const handleChange = (e) => { 
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
    
    // Auto-set leadStatus when status changes
    if (name === 'status') {
      const statusMap = {
        'Open': 'New Lead',
        'In Progress': 'Follow up Running',
        'Technical Visit Required': 'Follow up Running',
        'Technical Engineer Visited': 'Follow up Running',
        'Closed': 'Lost',
        'Successful': 'Completed & Review Collected'
      };
      setForm(prev => ({ 
        ...prev, 
        [name]: value,
        leadStatus: statusMap[value] || prev.leadStatus 
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const basePayload = {
        employee_name: employee.employee_name,
        employee_id: employee.employee_id,
        product_name: form.category, // Store category as product_name
        customer_name: form.customer_name,
        entity_type: form.entityType,
        follow_up_type: form.followUpType,
        next_follow_up: form.nextFollowUp ? form.nextFollowUp.replace("T", " ") : null,
        purpose: form.purpose,
        assigned_to: form.assignedTo,
        follow_up_status: form.status,
        lead_status: form.leadStatus || form.status,
        outcome: form.outcome,
      };
      
      const url = isNewRoot ? `${SERVER_URL}/Rahul/insertNewFollowUp.php` : `${SERVER_URL}/Rahul/updateFollowUp.php`;
      const payload = isNewRoot ? basePayload : { ...basePayload, follow_up_id: selectedFollowUpId };
      
      const res = await fetch(url, { 
        method: "POST", 
        headers: { "Content-Type": "application/json" }, 
        body: JSON.stringify(payload) 
      });
      const data = await res.json();
      
      if (!data.status) { 
        alert(data.message || "Operation failed"); 
        return; 
      }
      
      setForm(emptyForm);
      
      if (isNewRoot) { 
        await fetchLatest(); 
        setView("list"); 
      } else { 
        await fetchTimeline(selectedFollowUpId); 
        setView("timeline"); 
      }
      
      if (onRefresh) {
        onRefresh();
      }
      
      alert(data.message || "Success");
      
    } catch (err) { 
      console.error("submit error:", err); 
      alert("Error submitting form");
    }
  };

  if (!isOpen) return null;

  const latestColumns = [
    { header: "FollowUp ID", key: "follow_up_id" },
    { header: "Date", key: "follow_up_date_time" },
    { header: "Employee", key: "employee_name" },
    { header: "Category", key: "category" },
    { header: "Customer", key: "customer_name" },
    { header: "Entity", key: "entity_type" },
    { header: "Type", key: "follow_up_type" },
    { header: "Next Follow Up", key: "next_follow_up" },
    { header: "Purpose", key: "purpose" },
    { header: "Status", key: "follow_up_status" },
    { header: "Lead Status", key: "lead_status" },
  ];

  const timelineColumns = [
    { header: "Date", key: "follow_up_date_time" },
    { header: "Category", key: "category" },
    { header: "Customer", key: "customer_name" },
    { header: "Type", key: "follow_up_type" },
    { header: "Purpose", key: "purpose" },
    { header: "Assigned To", key: "assigned_to" },
    { header: "Outcome", key: "outcome" },
    { header: "Next Follow Up", key: "next_follow_up" },
    { header: "Status", key: "follow_up_status" },
    { header: "Lead Status", key: "lead_status" },
  ];

  /* ════════════════════════════════ JSX ════════════════════════════════ */
  return (
    <>
      <style>{`
        .att-scrollbar::-webkit-scrollbar { width: 5px; height: 5px; }
        .att-scrollbar::-webkit-scrollbar-track { background: #f1f5f9; border-radius: 10px; }
        .att-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
        .att-scrollbar::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
      `}</style>

      <div className="fixed inset-0 z-50 flex items-center justify-center p-2"
        style={{ background: "rgba(15,23,42,0.45)", backdropFilter: "blur(6px)" }}>
        <div
          className="w-full max-w-5xl rounded-2xl flex flex-col max-h-[90vh] bg-white border border-slate-200"
          style={{ boxShadow: "0 20px 60px rgba(15,23,42,0.18)" }}
        >

          {/* HEADER */}
          <div
            className="px-6 py-4 flex justify-between items-center border-b border-slate-200 rounded-t-2xl"
            style={{ background: "#f8fafc" }}
          >
            <div className="flex items-center gap-3">
              {view !== "list" && (
                <button
                  onClick={() => setView("list")}
                  className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-200 transition-colors"
                >
                  <ArrowLeftIcon className="w-5 h-5" />
                </button>
              )}
              <div className="flex items-center gap-2.5">
                <div className="w-1 h-6 rounded-full"
                  style={{ background: "linear-gradient(180deg, #1e40af, #0369a1)" }} />
                <h2 className="text-lg font-bold text-slate-800">
                  {view === "list" && "Follow-up History"}
                  {view === "timeline" && "Follow-up Timeline"}
                  {view === "form" && (isNewRoot ? "New Follow-up" : "Add Follow-up")}
                </h2>
              </div>
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors">
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          {/* BODY */}
          <div className="flex-1 overflow-y-auto p-5 att-scrollbar">

            {/* ── LIST VIEW ── */}
            {view === "list" && (
              <>
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4 p-3 rounded-xl border border-slate-200 bg-slate-50">
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => { setIsNewRoot(true); setForm(emptyForm); setView("form"); }}
                      className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold text-white transition-all hover:opacity-90"
                      style={{
                        background: "linear-gradient(135deg, #1e40af, #0369a1)",
                        boxShadow: "0 2px 8px rgba(30,64,175,0.18)",
                      }}
                    >
                      <PlusIcon className="w-4 h-4" />
                      Add Follow-up
                    </button>
                  </div>

                  <div className="flex items-center gap-4 flex-wrap">
                    <div className="flex flex-col gap-1">
                      <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Select Month</label>
                      <input
                        type="month"
                        value={selectedMonth}
                        onChange={(e) => setSelectedMonth(e.target.value)}
                        className="bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
                      />
                    </div>
                    <div className="text-xs font-bold text-slate-600 bg-white px-3 py-1.5 rounded-lg border border-slate-200">
                      Total: <span className="text-slate-800">{totalCount}</span>
                    </div>
                    <ExcelExport data={filteredLatest} columns={latestColumns} fileName="FollowUpHistory.xlsx" reportTitle="Follow Up History" />
                  </div>
                </div>

                <div className="overflow-x-auto max-h-[55vh] overflow-y-auto rounded-xl border border-slate-200 att-scrollbar">
                  <table className="min-w-[900px] w-full text-sm border-collapse">
                    <thead className="sticky top-0 z-20" style={{ background: "#f8fafc" }}>
                      <tr>
                        {latestColumns.map((col) => (
                          <th key={col.key} className="p-3 text-left border-b border-slate-200">
                            {["follow_up_date_time", "next_follow_up", "purpose", "lead_status", "category"].includes(col.key) ? (
                              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{col.header}</span>
                            ) : (
                              <TableHeaderFilter
                                label={col.header}
                                options={getColumnOptions(col.key)}
                                selectedValues={filters[col.key] || []}
                                onChange={(vals) => setFilters((prev) => ({ ...prev, [col.key]: vals }))}
                              />
                            )}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredLatest.map((fu) => (
                        <tr
                          key={fu.follow_up_id}
                          className="border-t border-slate-100 cursor-pointer transition-colors hover:bg-blue-50/60"
                          onClick={() => {
                            setSelectedFollowUpId(fu.follow_up_id);
                            setForm({ 
                              ...emptyForm, 
                              category: fu.category || fu.product_name,
                              product_name: fu.product_name,
                              customer_name: fu.customer_name, 
                              entityType: fu.entity_type,
                              status: fu.follow_up_status,
                              leadStatus: fu.lead_status || fu.follow_up_status,
                            });
                            fetchTimeline(fu.follow_up_id);
                            setView("timeline");
                          }}
                        >
                          {latestColumns.map((col) => (
                            <td key={col.key} className="p-3 text-slate-500 text-xs">
                              {col.key === "follow_up_status" || col.key === "lead_status" ? (
                                <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${getStatusStyle(fu[col.key])}`}>
                                  {fu[col.key] || fu.follow_up_status}
                                </span>
                              ) : col.key === "category" ? (
                                fu.category || fu.product_name || '—'
                              ) : fu[col.key] || '—'}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {/* ── TIMELINE VIEW ── */}
            {view === "timeline" && (
              <>
                <div className="flex justify-between items-center mb-4">
                  <button
                    onClick={() => { setIsNewRoot(false); setView("form"); }}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold text-white transition-all hover:opacity-90"
                    style={{
                      background: "linear-gradient(135deg, #1e40af, #0369a1)",
                      boxShadow: "0 2px 8px rgba(30,64,175,0.18)",
                    }}
                  >
                    <PlusIcon className="w-4 h-4" />
                    ADD
                  </button>
                  <ExcelExport data={timeline} columns={timelineColumns} fileName="FollowUpTimeline.xlsx" reportTitle="Follow Up Timeline" />
                </div>

                <div className="overflow-x-auto max-h-[55vh] overflow-y-auto rounded-xl border border-slate-200 att-scrollbar">
                  <table className="min-w-[800px] w-full text-sm border-collapse">
                    <thead className="sticky top-0 z-20" style={{ background: "#f8fafc" }}>
                      <tr>
                        {timelineColumns.map((col) => (
                          <th key={col.key} className="p-3 text-left border-b border-slate-200">
                            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{col.header}</span>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {timeline.map((t, i) => (
                        <tr key={i} className="border-t border-slate-100">
                          {timelineColumns.map((col) => (
                            <td key={col.key} className="p-3 text-slate-500 text-xs">
                              {col.key === "follow_up_status" || col.key === "lead_status" ? (
                                <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${getStatusStyle(t[col.key])}`}>
                                  {t[col.key] || t.follow_up_status}
                                </span>
                              ) : col.key === "category" ? (
                                t.category || t.product_name || '—'
                              ) : t[col.key] || '—'}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {/* ── FORM VIEW ── */}
            {view === "form" && (
              <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">

                {/* Category */}
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">
                    Category <span className="text-rose-500">*</span>
                  </label>
                  <SearchableDropdown
                    label=""
                    options={categoryOptions}
                    value={form.category}
                    onChange={(val) => {
                      setForm(prev => ({ 
                        ...prev, 
                        category: val,
                        product_name: '' // Reset product when category changes
                      }));
                    }}
                    placeholder="Select Category"
                    required
                  />
                </div>

                {/* Product (filtered by category) */}
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">
                    Product <span className="text-rose-500">*</span>
                  </label>
                  <SearchableDropdown
                    label=""
                    options={getFilteredProducts(form.category)}
                    value={form.product_name}
                    onChange={(val) => setForm({ ...form, product_name: val })}
                    placeholder={form.category ? "Select Product" : "Select category first"}
                    disabled={!form.category}
                    required
                  />
                </div>

                {/* Customer */}
                {isNewRoot ? (
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">
                      Customer <span className="text-rose-500">*</span>
                    </label>
                    <SearchableDropdown
                      label=""
                      options={customerOptions}
                      value={form.customer_name}
                      onChange={(val) => setForm({ ...form, customer_name: val })}
                      placeholder={form.product_name ? "Select customer" : "Select product first"}
                      disabled={!form.product_name}
                      required
                    />
                  </div>
                ) : (
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Customer</label>
                    <input value={form.customer_name} disabled className={inputStyle} />
                  </div>
                )}

                {/* Entity */}
                {isNewRoot ? (
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">
                      Entity <span className="text-rose-500">*</span>
                    </label>
                    <SearchableDropdown 
                      label="" 
                      options={entityOptions} 
                      value={form.entityType} 
                      onChange={(val) => setForm({ ...form, entityType: val })} 
                      placeholder="Select Entity"
                      required
                    />
                  </div>
                ) : (
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Entity</label>
                    <input value={form.entityType} disabled className={inputStyle} />
                  </div>
                )}

                {/* Next Follow Up */}
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Next Follow Up</label>
                  <input type="datetime-local" name="nextFollowUp" value={form.nextFollowUp} onChange={handleChange} className={inputStyle} />
                </div>

                {/* Follow-up Type */}
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">
                    Follow-up Type <span className="text-rose-500">*</span>
                  </label>
                  <SearchableDropdown 
                    label="" 
                    options={followUpTypeOptions} 
                    value={form.followUpType} 
                    onChange={(val) => setForm({ ...form, followUpType: val })} 
                    required
                  />
                </div>

                {/* Purpose */}
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">
                    Purpose <span className="text-rose-500">*</span>
                  </label>
                  <input name="purpose" value={form.purpose} onChange={handleChange} placeholder="Purpose" className={inputStyle} required />
                </div>

                {/* Assigned To */}
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Assigned To</label>
                  <SearchableDropdown 
                    label="" 
                    options={assignedToOptions} 
                    value={form.assignedTo} 
                    onChange={(val) => setForm({ ...form, assignedTo: val })} 
                    placeholder="Select Employee" 
                  />
                </div>

                {/* Status */}
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">
                    Status <span className="text-rose-500">*</span>
                  </label>
                  <SearchableDropdown 
                    label="" 
                    options={statusOptions} 
                    value={form.status} 
                    onChange={(val) => setForm({ ...form, status: val })} 
                    required
                  />
                </div>

                {/* Lead Status (for syncing) */}
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Lead Status (Sync)</label>
                  <SearchableDropdown 
                    label="" 
                    options={leadStatusOptions} 
                    value={form.leadStatus} 
                    onChange={(val) => setForm({ ...form, leadStatus: val })} 
                    placeholder="Select Lead Status" 
                  />
                </div>

                {/* Outcome */}
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">
                    Outcome <span className="text-rose-500">*</span>
                  </label>
                  <input name="outcome" value={form.outcome} onChange={handleChange} placeholder="Outcome" className={inputStyle} required />
                </div>

                {/* Actions */}
                <div className="col-span-full flex justify-end gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setView(isNewRoot ? "list" : "timeline")}
                    className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-600 border border-slate-200 hover:bg-slate-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-5 py-2 rounded-lg text-sm font-bold text-white transition-all hover:opacity-90"
                    style={{
                      background: "linear-gradient(135deg, #1e40af, #0369a1)",
                      boxShadow: "0 2px 8px rgba(30,64,175,0.18)",
                    }}
                  >
                    Submit
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default FollowUpModal;